/**
 * NoteEditor — the detail pane: save status, Drive-sync indicator, pin/archive/
 * delete tools, a plain-text meta line, and a borderless <textarea>. No
 * formatting toolbar (plain text only — design invariant).
 */
import { useEffect, useRef } from 'react';
import { formatRelativeTime } from '@/lib/timeUtils';
import type { SaveStatus } from '../hooks/useNoteAutoSave';
import type { NoteObject } from '../notesShared';

interface NoteEditorProps {
  note: NoteObject;
  /** Whether the open note is archived. */
  archived: boolean;
  draft: string;
  onDraftChange: (body: string) => void;
  status: SaveStatus;
  /** Epoch ms of the last successful autosave (for an accurate "Saved · just now"). */
  savedAt: number | null;
  onTogglePin: () => void;
  onToggleArchive: () => void;
  onDelete: () => void;
}

function fullDate(iso: string | null | undefined): string {
  const ts = Date.parse(iso || '');
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function NoteEditor({
  note,
  archived,
  draft,
  onDraftChange,
  status,
  savedAt,
  onTogglePin,
  onToggleArchive,
  onDelete,
}: NoteEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Focus a freshly-opened empty note so the user can type immediately.
  useEffect(() => {
    if (note && (note.body ?? '') === '') {
      textareaRef.current?.focus();
    }
  }, [note.id]);

  // Prefer the in-session save time (accurate "just now") over the cached
  // note.updatedAt, which lags until the list refetch lands.
  const savedTime = savedAt ? new Date(savedAt).toISOString() : note.updatedAt;
  const statusText = status === 'saving' ? 'Saving…' : `Saved · ${formatRelativeTime(savedTime) || 'just now'}`;

  return (
    <section className="notes-editor">
      <div className="notes-ed-bar">
        <div className={`notes-ed-status${status === 'saving' ? ' is-saving' : ''}`}>
          <span className="notes-ed-dot" />
          <span>{statusText}</span>
        </div>
        <div className="notes-ed-tools">
          {note.googleDriveFileId ? (
            <span className="notes-ed-drive">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M8 17l4-7 4 7M8 17H4l4-7M16 17h4l-4-7" />
              </svg>
              Synced to Drive
            </span>
          ) : null}
          <span className="notes-ed-sep" />
          <button
            type="button"
            className={`notes-tool${note.pinned ? ' is-active' : ''}`}
            onClick={onTogglePin}
            title={note.pinned ? 'Unpin' : 'Pin'}
            aria-pressed={Boolean(note.pinned)}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill={note.pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M14 4v5l3 3v2h-5v6l-1 1-1-1v-6H5v-2l3-3V4z" />
            </svg>
          </button>
          <button
            type="button"
            className="notes-tool"
            onClick={onToggleArchive}
            title={archived ? 'Unarchive' : 'Archive'}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="4" rx="1" />
              <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
            </svg>
          </button>
          <button type="button" className="notes-tool is-danger" onClick={onDelete} title="Delete">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
            </svg>
          </button>
        </div>
      </div>

      <div className="notes-ed-scroll xv-scroll">
        <div className="notes-ed-body">
          <div className="notes-ed-meta">{fullDate(note.updatedAt)} · plain text</div>
          <textarea
            ref={textareaRef}
            className="notes-ed-text"
            value={draft}
            placeholder="Start typing…"
            spellCheck
            onChange={(e) => onDraftChange(e.target.value)}
            // Escape exits the inline editor first (issue #3938, AC#4): consume
            // the key (preventDefault + stopPropagation) and blur the field so
            // this first Esc is a meaningful "leave the note body" action and the
            // Notes module pane survives this keypress. The autosave hook has
            // already persisted the draft, so blurring loses nothing — a
            // subsequent Esc (focus now off the textarea) closes the pane.
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.blur();
              }
            }}
          />
        </div>
      </div>
    </section>
  );
}
